import * as React from 'react';
import { useState, useCallback } from 'react';
import styled from 'styled-components';

import { Link } from 'components/common/router';
import { isPermitted } from 'util/PermissionsMixin';
import { DataTable, NoSearchResult, PaginatedList, QueryHelper, SearchForm, HoverForHelp } from 'components/common';
import type { SearchFilterUsageEntity } from 'search-filter/types';
import useCurrentUser from 'hooks/useCurrentUser';
import useSearchFilterUsagesQuery from 'search-filter/hooks/useSearchFilterUsagesQuery';
import { FILTER_USAGE_ENTITY_TYPE } from 'search-filter/constants';
import StringUtils from 'util/StringUtils';
import useShowRouteForEntity from 'routing/hooks/useShowRouteForEntity';

const TABLE_HEADERS = ['Name', 'Capability'];
const DEFAULT_QUERY = {
  query: '',
  per_page: 10,
  page: 1,
  sort: 'title',
  order: 'asc',
};

const StyledPaginatedList = styled(PaginatedList)`
  .pagination {
    margin: 0;
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
`;

const StyledHoverForHelp = styled((props: any) => <HoverForHelp {...props} />)`
  margin-left: 5px;
`;

const FilterUsage = ({ id, type, title, capability }: SearchFilterUsageEntity) => {
  const currentUser = useCurrentUser();
  let hasPermissions: boolean;
  let entityRoute: string;
  const eventDefinitionRoute = useShowRouteForEntity(id, 'EVENT_DEFINITION');

  switch (type) {
    case FILTER_USAGE_ENTITY_TYPE.EVENT_DEFINITION:
      hasPermissions = isPermitted(currentUser.permissions, `eventdefinitions:read:${id}`);
      entityRoute = eventDefinitionRoute;
      break;
    case FILTER_USAGE_ENTITY_TYPE.SIGMA_RULE:
      hasPermissions = isPermitted(currentUser.permissions, `sigma_rules:read:${id}`);
      entityRoute = '/security/sigma';
      break;
    default:
      return <div />;
  }

  return (
    <tr key={id}>
      <td className="limited">
        <Title>
          {hasPermissions ? <Link to={entityRoute}>{title}</Link> : title}
          {capability === 'None' && (
            <StyledHoverForHelp title="Missing Permissions">
              This {type.toLowerCase()} is including the search filter, but you do not have any permissions for it.
            </StyledHoverForHelp>
          )}
        </Title>
      </td>
      <td>{capability}</td>
    </tr>
  );
};

type Props = {
  filterId: string;
  type: string;
};

const FilterUsages = ({ filterId, type }: Props) => {
  const [filterQuery, setSearchFilter] = useState(DEFAULT_QUERY);
  const { data } = useSearchFilterUsagesQuery(filterId, { ...filterQuery, type: type });

  const handlePaginationChange = useCallback(
    (page: number) => {
      setSearchFilter((cur) => ({ ...cur, page }));
    },
    [setSearchFilter],
  );

  const handleSearch = useCallback(
    (newSearchQuery: string | undefined) =>
      setSearchFilter((cur) => ({
        ...cur,
        page: DEFAULT_QUERY.page,
        query: newSearchQuery,
      })),
    [],
  );
  const handleReset = useCallback(() => handleSearch(DEFAULT_QUERY.query), [handleSearch]);
  const renderOverviewItem = useCallback((entity: SearchFilterUsageEntity) => <FilterUsage {...entity} />, []);

  return (
    <>
      <p className="description">
        Found {data.pagination.total}{' '}
        {StringUtils.pluralize(data.pagination.total, 'usage which includes', 'usages which include')} this search
        filter.
      </p>

      <StyledPaginatedList
        activePage={filterQuery.page}
        totalItems={data.pagination.total}
        onChange={handlePaginationChange}>
        <DataTable
          className="table-hover"
          customFilter={
            <SearchForm
              onSearch={handleSearch}
              queryHelpComponent={<QueryHelper entityName="title" commonFields={['title']} />}
              onReset={handleReset}
              topMargin={0}
            />
          }
          dataRowFormatter={renderOverviewItem}
          noDataText={<NoSearchResult>No usages have been found.</NoSearchResult>}
          headers={TABLE_HEADERS}
          id="shared-entities"
          rowClassName="no-bm"
          rows={data.elements}
          sortByKey="title"
        />
      </StyledPaginatedList>
    </>
  );
};

export default FilterUsages;
